import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePublish } from '../../lib/usePublish';
import type { Page, PageVersion } from '../../lib/types';
import Modal from '../../components/ui/Modal';
import PageCreateForm from '../../components/forms/PageCreateForm';
import PublishButton from '../../components/ui/PublishButton';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';

// ─── Icons ─────────────────────────────────────────────────────────────────────
function PencilIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}


// ─── Grouping ──────────────────────────────────────────────────────────────────
interface PageGroup {
  pageKey: string | null;
  pages: Page[];
}

function groupPages(pages: Page[]): PageGroup[] {
  const keyed = new Map<string, Page[]>();
  const standalone: Page[] = [];

  for (const page of pages) {
    if (page.pageKey) {
      const bucket = keyed.get(page.pageKey) ?? [];
      bucket.push(page);
      keyed.set(page.pageKey, bucket);
    } else {
      standalone.push(page);
    }
  }

  const groups: PageGroup[] = [];
  for (const [pageKey, ps] of keyed) {
    groups.push({ pageKey, pages: ps.sort((a) => (a.locale === 'es' ? -1 : 1)) });
  }
  for (const page of standalone) {
    groups.push({ pageKey: null, pages: [page] });
  }
  return groups;
}

// ─── Status ────────────────────────────────────────────────────────────────────
type GroupStatus = 'published' | 'incomplete' | 'draft';

function getGroupStatus(group: PageGroup): GroupStatus {
  const { pages, pageKey } = group;
  if (!pageKey) return pages[0]?.status === 'published' ? 'published' : 'draft';
  if (pages.length < 2) return 'incomplete';
  if (pages.every((p) => p.status === 'published')) return 'published';
  if (pages.some((p) => p.status === 'published')) return 'incomplete';
  return 'draft';
}

function StatusDot({ status }: { status: GroupStatus }) {
  const cls = { published: 'bg-ui-success', incomplete: 'bg-ui-warning', draft: 'bg-ui-text-subtle' }[status];
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cls}`} />;
}

function StatusLabel({ status }: { status: GroupStatus }) {
  const label = { published: 'publicada', incomplete: 'incompleta', draft: 'borrador' }[status];
  const cls = { published: 'text-ui-success', incomplete: 'text-ui-warning', draft: 'text-ui-text-subtle' }[status];
  return <span className={`text-xs ${cls}`}>{label}</span>;
}

// ─── Edit / create buttons ─────────────────────────────────────────────────────
function EditLocaleButton({ page, onClick }: { page: Page; onClick: () => void }) {
  const published = page.status === 'published';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors border ${
        published
          ? 'bg-ui-success/10 text-ui-success border-ui-success/30 hover:bg-ui-success/15'
          : 'bg-ui-warning/10 text-ui-warning border-ui-warning/30 hover:bg-ui-warning/15'
      }`}
    >
      <PencilIcon />
      Editar {page.locale.toUpperCase()}
    </button>
  );
}

function CreateLocaleButton({ locale, onClick }: { locale: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-dashed border-ui-border text-ui-text-subtle hover:border-ui-accent hover:text-ui-accent transition-colors"
    >
      <span className="text-sm leading-none">+</span>
      Crear {locale.toUpperCase()}
    </button>
  );
}

// ─── Create context ────────────────────────────────────────────────────────────
export interface CreateContext {
  locale?: string;
  pageKey?: string;
  siblingTitle?: string;
}

// ─── Page card ─────────────────────────────────────────────────────────────────
function PageCard({
  group,
  multilingual,
  onNavigate,
  onAddLocale,
  onDelete,
  onHistory,
}: {
  group: PageGroup;
  multilingual: boolean;
  onNavigate: (id: string) => void;
  onAddLocale: (ctx: CreateContext) => void;
  onDelete: (group: PageGroup) => void;
  onHistory: (pages: Page[]) => void;
}) {
  // Solo se muestran las opciones ES/EN si el sitio es multi-idioma (según el plan).
  // En sitios monolingües, una página con pageKey se trata como una página normal.
  const isBilingual = !!group.pageKey && multilingual;
  const esPage = group.pages.find((p) => p.locale === 'es');
  const enPage = group.pages.find((p) => p.locale === 'en');
  const title = esPage?.title ?? enPage?.title ?? group.pages[0]?.title ?? '—';
  const status = multilingual ? getGroupStatus(group) : getGroupStatus({ ...group, pageKey: null });

  return (
    <div className="hover:bg-ui-text/[0.04] transition-colors">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <StatusDot status={status} />
          <span className="text-sm text-ui-text truncate">{title}</span>
          {!isBilingual && group.pages[0] && (
            <span className="text-xs text-ui-text-subtle font-mono hidden sm:inline">{group.pages[0].slug}</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusLabel status={status} />

          {/* Bilingual: trash inline in header; standalone: trash also inline */}
          {isBilingual && (
            <button
              onClick={() => onDelete(group)}
              className="p-1.5 rounded text-ui-text-muted hover:text-ui-danger hover:bg-ui-danger/10 transition-colors"
              title="Eliminar"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* Standalone action row */}
      {!isBilingual && group.pages[0] && (
        <div className="flex items-center gap-2 px-4 pb-3 pl-[2.375rem]">
          <button
            onClick={() => onNavigate(group.pages[0].id)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors bg-ui-surface-2 border border-ui-border text-ui-text hover:border-ui-border-strong"
          >
            <PencilIcon />
            Editar
          </button>
          <button
            onClick={() => onHistory([group.pages[0]])}
            className="px-2.5 py-1.5 rounded-md text-xs text-ui-text-muted hover:text-ui-text border border-ui-border hover:border-ui-border-strong transition-colors"
          >
            Historial
          </button>
          <button
            onClick={() => onDelete(group)}
            className="p-1.5 rounded text-ui-text-muted hover:text-ui-danger hover:bg-ui-danger/10 transition-colors ml-auto"
            title="Eliminar"
          >
            <TrashIcon />
          </button>
        </div>
      )}

      {/* Locale action row (bilingual only) */}
      {isBilingual && (
        <div className="flex items-center gap-2 px-4 pb-3 pl-[2.375rem]">
          {esPage
            ? <EditLocaleButton page={esPage} onClick={() => onNavigate(esPage.id)} />
            : <CreateLocaleButton locale="es" onClick={() => onAddLocale({ locale: 'es', pageKey: group.pageKey!, siblingTitle: enPage?.title })} />
          }
          {enPage
            ? <EditLocaleButton page={enPage} onClick={() => onNavigate(enPage.id)} />
            : <CreateLocaleButton locale="en" onClick={() => onAddLocale({ locale: 'en', pageKey: group.pageKey!, siblingTitle: esPage?.title })} />
          }
          {(esPage || enPage) && (
            <button
              onClick={() => onHistory(group.pages)}
              className="px-2.5 py-1.5 rounded-md text-xs text-ui-text-muted hover:text-ui-text border border-ui-border hover:border-ui-border-strong transition-colors ml-1"
            >
              Historial
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── History modal ─────────────────────────────────────────────────────────────
function formatDate(raw: Date | string) {
  const d = new Date(raw);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Hoy ${time}`;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) + ` · ${time}`;
}

function blockPreview(contentJson: string): { count: number; text: string } {
  try {
    const blocks = JSON.parse(contentJson) as { type: string; props: Record<string, unknown> }[];
    if (!blocks.length) return { count: 0, text: '' };
    const p = blocks[0].props as Record<string, string>;
    const text = p.headline ?? p.title ?? p.eyebrow ?? '';
    return { count: blocks.length, text };
  } catch {
    return { count: 0, text: '' };
  }
}

function HistoryVersionList({ page, onRestored, onClose }: { page: Page; onRestored: () => void; onClose: () => void }) {
  const [versions, setVersions] = useState<PageVersion[] | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    setVersions(null);
    setConfirmId(null);
    api.pages.versions(page.id)
      .then((vs) => setVersions([...vs].reverse()))
      .catch(() => setVersions([]));
  }, [page.id]);

  async function handleRestore(v: PageVersion) {
    setRestoring(true);
    try {
      await api.pages.saveContent(page.id, v.contentJson);
      onRestored();
      onClose();
    } finally {
      setRestoring(false);
    }
  }

  if (versions === null) return <p className="text-sm text-ui-text-subtle text-center py-8">Cargando historial…</p>;
  if (versions.length === 0) return <p className="text-sm text-ui-text-subtle text-center py-8">Sin versiones guardadas.</p>;

  return (
    <div className="space-y-2">
      {versions.map((v, i) => {
        const { count, text } = blockPreview(v.contentJson);
        const isEmpty = count === 0;
        const isCurrent = i === 0;
        const isConfirming = confirmId === v.id;

        return (
          <div
            key={v.id}
            className={`rounded-md border px-4 py-3 ${
              isCurrent
                ? 'border-ui-accent/40 bg-ui-accent/5'
                : 'border-ui-border bg-ui-bg'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-ui-text font-medium">{formatDate(v.createdAt)}</span>
                  {isCurrent && (
                    <span className="text-xs bg-ui-accent/20 text-ui-accent px-1.5 py-0.5 rounded font-medium">
                      más reciente
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {isEmpty ? (
                    <span className="text-xs text-ui-warning">⚠ Sin bloques (vacío)</span>
                  ) : (
                    <span className="text-xs text-ui-text-subtle">{count} {count === 1 ? 'bloque' : 'bloques'}</span>
                  )}
                  {text && <span className="text-xs text-ui-text-subtle truncate max-w-[200px]">· "{text}"</span>}
                </div>
                <p className="text-xs text-ui-text-subtle mt-0.5">{v.publishedBy}</p>
              </div>

              <div className="flex-shrink-0">
                {isConfirming ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ui-text-muted">¿Restaurar?</span>
                    <button
                      disabled={restoring}
                      onClick={() => handleRestore(v)}
                      className="px-2.5 py-1 text-xs font-medium bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse rounded transition-colors disabled:opacity-50"
                    >
                      {restoring ? '…' : 'Sí'}
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-2.5 py-1 text-xs text-ui-text-muted hover:text-ui-text transition-colors"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(v.id)}
                    disabled={isCurrent}
                    className="px-3 py-1.5 text-xs text-ui-text-muted hover:text-ui-text border border-ui-border hover:border-ui-border-strong rounded transition-colors disabled:opacity-30 disabled:cursor-default"
                    title={isCurrent ? 'Esta es la versión actual' : 'Restaurar esta versión'}
                  >
                    Restaurar
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryModal({ pages, onClose, onRestored }: { pages: Page[]; onClose: () => void; onRestored: () => void }) {
  const sortedPages = [...pages].sort((a) => (a.locale === 'es' ? -1 : 1));
  const [activeLocale, setActiveLocale] = useState(sortedPages[0]?.locale ?? 'es');
  const activePage = sortedPages.find((p) => p.locale === activeLocale) ?? sortedPages[0];
  const isBilingual = sortedPages.length > 1;
  const title = sortedPages.find((p) => p.locale === 'es')?.title ?? sortedPages[0]?.title ?? '—';

  return (
    <Modal title={`Historial — ${title}`} onClose={onClose}>
      {isBilingual && (
        <div className="flex gap-1 mb-4 p-1 bg-ui-bg rounded-lg border border-ui-border">
          {sortedPages.map((p) => (
            <button
              key={p.locale}
              onClick={() => setActiveLocale(p.locale)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeLocale === p.locale
                  ? 'bg-ui-surface-2 text-ui-text border border-ui-border-strong'
                  : 'text-ui-text-subtle hover:text-ui-text-muted'
              }`}
            >
              {p.locale.toUpperCase()} — {p.title}
            </button>
          ))}
        </div>
      )}
      <div className="max-h-[55vh] overflow-y-auto pr-1">
        {activePage && (
          <HistoryVersionList key={activePage.id} page={activePage} onRestored={onRestored} onClose={onClose} />
        )}
      </div>
    </Modal>
  );
}

// ─── Delete modal ──────────────────────────────────────────────────────────────
function DeleteModal({
  group,
  deleting,
  onDelete,
  onClose,
}: {
  group: PageGroup;
  deleting: boolean;
  onDelete: (ids: string[]) => void;
  onClose: () => void;
}) {
  const esPage = group.pages.find((p) => p.locale === 'es');
  const enPage = group.pages.find((p) => p.locale === 'en');
  const title = esPage?.title ?? enPage?.title ?? group.pages[0]?.title ?? '—';
  const isBilingual = !!group.pageKey && group.pages.length >= 2;

  const btnBase = 'w-full flex items-center justify-between px-4 py-3 rounded-md border text-sm text-left transition-colors disabled:opacity-50';

  return (
    <Modal title="Eliminar página" onClose={onClose}>
      <div className="space-y-4">
        {isBilingual ? (
          <>
            <p className="text-sm text-ui-text-muted">
              ¿Qué deseas eliminar de <span className="text-ui-text font-medium">"{title}"</span>?
            </p>
            <div className="space-y-2">
              {esPage && (
                <button
                  disabled={deleting}
                  onClick={() => onDelete([esPage.id])}
                  className={`${btnBase} border-ui-border bg-ui-bg hover:border-red-500/50 hover:bg-red-500/5 text-ui-text-muted`}
                >
                  <span>Solo versión <strong>ES</strong></span>
                  <span className="text-xs text-ui-text-subtle truncate ml-2">{esPage.title}</span>
                </button>
              )}
              {enPage && (
                <button
                  disabled={deleting}
                  onClick={() => onDelete([enPage.id])}
                  className={`${btnBase} border-ui-border bg-ui-bg hover:border-red-500/50 hover:bg-red-500/5 text-ui-text-muted`}
                >
                  <span>Solo versión <strong>EN</strong></span>
                  <span className="text-xs text-ui-text-subtle truncate ml-2">{enPage.title}</span>
                </button>
              )}
              <button
                disabled={deleting}
                onClick={() => onDelete(group.pages.map((p) => p.id))}
                className={`${btnBase} border-ui-danger/30 bg-ui-danger/10 hover:bg-ui-danger/15 text-ui-danger`}
              >
                <span>Toda la página <span className="font-medium">(ambas versiones)</span></span>
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-ui-text-muted">
              ¿Eliminar <span className="text-ui-text font-medium">"{title}"</span>? Esta acción no se puede deshacer.
            </p>
            <button
              disabled={deleting}
              onClick={() => onDelete(group.pages.map((p) => p.id))}
              className={`${btnBase} border-ui-danger/30 bg-ui-danger/10 hover:bg-ui-danger/15 text-ui-danger`}
            >
              {deleting ? 'Eliminando…' : 'Sí, eliminar'}
            </button>
          </>
        )}

        <button
          onClick={onClose}
          disabled={deleting}
          className="w-full px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
}

// ─── PagesTabContent — reutilizable en SitePage ────────────────────────────────
export function PagesTabContent({ showPublish = true }: { showPublish?: boolean } = {}) {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createCtx, setCreateCtx] = useState<CreateContext>({});
  const [deleteTarget, setDeleteTarget] = useState<PageGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<Page[] | null>(null);
  // Si el sitio es multi-idioma (depende del plan), la lista ofrece versiones ES/EN.
  const [multilingual, setMultilingual] = useState(false);
  const { last: lastDeploy, publish } = usePublish();

  // Las páginas legales (page_key legal-*) se gestionan en Mi Sitio → Avisos legales,
  // no en la lista general de páginas.
  const loadPages = () =>
    api.pages.list()
      .then((all) => setPages(all.filter((p) => !p.pageKey?.startsWith('legal-'))))
      .catch(console.error);

  useEffect(() => {
    loadPages();
    api.settings.capabilities()
      .then((cap) => setMultilingual(cap.multilingual))
      .catch(() => setMultilingual(false));
  }, []);

  const groups = groupPages(pages);

  async function handleDelete(ids: string[]) {
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => api.pages.delete(id)));
      setPages((prev) => prev.filter((p) => !ids.includes(p.id)));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  function handlePageCreated(page: Page) {
    setShowCreate(false);
    navigate(`/editor/${page.id}`);
  }

  function openCreate(ctx: CreateContext = {}) {
    setCreateCtx(ctx);
    setShowCreate(true);
  }

  const modalTitle = createCtx.locale
    ? `Nueva versión ${createCtx.locale.toUpperCase()}${createCtx.siblingTitle ? ` — ${createCtx.siblingTitle}` : ''}`
    : 'Nueva página';

  return (
    <div className="space-y-5">
      {/* Publish action row — oculto cuando el contenedor (Mi Sitio) ya ofrece un botón fijo */}
      {showPublish && (
        <div className="flex items-center justify-end gap-4">
          {lastDeploy && (
            <div className="flex items-center gap-2 text-xs text-ui-text-subtle">
              <span className={`w-1.5 h-1.5 rounded-full ${
                lastDeploy.status === 'success' ? 'bg-ui-success' :
                lastDeploy.status === 'error' ? 'bg-ui-danger' : 'bg-ui-warning'
              }`} />
              Última publicación: <span className="capitalize">{lastDeploy.status === 'success' ? 'exitosa' : lastDeploy.status === 'error' ? 'con error' : 'en proceso'}</span>
            </div>
          )}
          <button
            onClick={() => setShowPublishConfirm(true)}
            className="px-4 py-1.5 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md transition-colors shadow-sm shadow-ui-accent/20"
          >
            Publicar sitio
          </button>
        </div>
      )}

      {/* Plantilla del sitio */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-ui-text-muted uppercase tracking-wider">Plantilla del sitio</span>
        </div>
        <div className="bg-ui-surface rounded-lg border border-ui-border divide-y divide-ui-border overflow-hidden">
          {[
            { label: 'Encabezado', desc: 'Menú de navegación y logo', path: '/editor/header' },
            { label: 'Pie de página', desc: 'Columnas de links y tagline', path: '/editor/footer' },
          ].map(({ label, desc, path }) => (
            <div key={path} className="flex items-center justify-between px-4 py-3 gap-4 hover:bg-ui-text/[0.04] transition-colors">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-ui-text-subtle" />
                <span className="text-sm text-ui-text">{label}</span>
                <span className="text-xs text-ui-text-subtle hidden sm:inline">{desc}</span>
              </div>
              <button onClick={() => navigate(path)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors bg-ui-surface-2 border border-ui-border text-ui-text hover:border-ui-border-strong shrink-0">
                <PencilIcon />
                Editar
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Page list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-ui-text-muted uppercase tracking-wider">
            {groups.length} {groups.length === 1 ? 'página' : 'páginas'}
          </span>
          <button onClick={() => openCreate()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md transition-colors shadow-sm shadow-ui-accent/20">
            <span className="text-base leading-none">+</span>
            Nueva página
          </button>
        </div>

        <div className="bg-ui-surface rounded-lg border border-ui-border divide-y divide-ui-border overflow-hidden">
          {groups.length === 0 && (
            <p className="px-4 py-8 text-sm text-ui-text-subtle text-center">
              Sin páginas.{' '}
              <button onClick={() => openCreate()} className="text-ui-accent hover:underline">
                Crea la primera
              </button>
            </p>
          )}
          {groups.map((group, i) => (
            <PageCard
              key={group.pageKey ?? `standalone-${i}`}
              group={group}
              multilingual={multilingual}
              onNavigate={(id) => navigate(`/editor/${id}`)}
              onAddLocale={openCreate}
              onDelete={setDeleteTarget}
              onHistory={(pages) => setHistoryTarget(pages)}
            />
          ))}
        </div>
      </div>

      {showCreate && (
        <PageCreateForm
          title={modalTitle}
          defaults={createCtx}
          multilingual={multilingual}
          onSave={handlePageCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {showPublishConfirm && (
        <Modal title="Publicar sitio" onClose={() => setShowPublishConfirm(false)}>
          <div className="space-y-4">
            <p className="text-sm text-ui-text-muted">
              Al publicar, <span className="text-ui-text font-medium">todos los cambios guardados</span> quedarán
              visibles para los visitantes del sitio.
            </p>
            <ul className="text-xs text-ui-text-subtle space-y-1 list-disc list-inside">
              <li>El proceso tarda aproximadamente 1–2 minutos</li>
              <li>El sitio actual sigue funcionando mientras se actualiza</li>
              <li>Al terminar verás aquí si fue exitoso o hubo algún problema</li>
            </ul>
            <div className="flex gap-3 pt-1">
              <PublishButton
                label="Sí, publicar ahora"
                className="flex-1 px-4 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
                onPublish={publish}
                onPublished={() => setShowPublishConfirm(false)}
                onError={() => setShowPublishConfirm(false)}
              />
              <button
                onClick={() => setShowPublishConfirm(false)}
                className="flex-1 px-4 py-2 border border-ui-border text-ui-text-muted hover:text-ui-text text-sm rounded-md transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <DeleteModal
          group={deleteTarget}
          deleting={deleting}
          onDelete={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {historyTarget && (
        <HistoryModal
          pages={historyTarget}
          onClose={() => setHistoryTarget(null)}
          onRestored={loadPages}
        />
      )}
    </div>
  );
}

// ─── Standalone page (ruta legacy; el acceso principal vive en /site) ─────────
export default function PagesPage() {
  return (
    <PageContainer>
      <PageHeader title="Páginas" />
      <ContextCard
        title="¿Qué es una página?"
        description="Es una sección completa de tu sitio, como Inicio, Nosotros, Contacto o Preguntas frecuentes. Dentro de cada página puedes organizar textos, imágenes y bloques de contenido."
        detail="Guardar conserva tus cambios en el panel. Para que los visitantes vean la versión más reciente, también debes usar «Publicar sitio»."
      />
      <PagesTabContent />
    </PageContainer>
  );
}
